import type { LandingComp } from "../marketing/HeroCanvas.engine";

/**
 * Real, anonymized comp figures for the hero animation — served by the new additive
 * `GET /api/next/landing/comps` endpoint (`server/landing.py`), backed by the `next_landing_comps`
 * table. This is the implementation guide's own stated non-negotiable (§0): "the hero must render
 * real, anonymized comp figures... the decoration itself becomes source-true."
 *
 * Deliberately NO client-side fallback array here (see HeroCanvas.engine.ts's file docstring,
 * deviation #2) — an empty response means the hero renders with no price labels, not fake ones.
 * A network failure is treated the same way: log it, return `[]`, never synthesize.
 *
 * DELIBERATELY does NOT go through `lib/auth.ts`'s `apiFetch` — that helper redirects to /login
 * on a 401, which is correct for the authenticated product screens but would be actively wrong
 * here: a landing-page visitor is, by definition, not signed in yet. This endpoint is public and
 * read-only (anonymized aggregate figures, no PII, no per-user data), so a plain unauthenticated
 * `fetch` is the right shape, not a bug.
 */
export async function fetchLandingComps(): Promise<LandingComp[]> {
  try {
    const res = await fetch("/api/next/landing/comps");
    if (!res.ok) throw new Error(`landing comps request failed (${res.status})`);
    const data = (await res.json()) as { comps?: LandingComp[] };
    return Array.isArray(data.comps) ? data.comps : [];
  } catch (err) {
    // A marketing decoration must never break the page (implementation guide §4's spirit) — but
    // failing open into FAKE data would violate the project's strict-tier rule, so we fail open
    // into NO data instead. The map/lens/network still render; only price labels are absent.
    console.warn("[landing] could not load real comp figures; hero will render without price labels", err);
    return [];
  }
}
